import React from "react";
import { motion } from "framer-motion";
import { Award, Maximize2, Lock, FileCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "../ui/dialog";
import { Reveal, Eyebrow, StaggerGroup, StaggerItem } from "./Reveal";
import { COURSE } from "../../data/course";

function CertCard({ cert, index }) {
  return (
    <StaggerItem>
      <Dialog>
        <DialogTrigger asChild>
          <button
            data-testid={`cert-card-${index}`}
            className="card-glow group relative block w-full overflow-hidden rounded-2xl border border-[#293891]/10 bg-white text-left shadow-sm"
          >
            <div className="relative overflow-hidden bg-[#F5F7FC]">
              <img
                src={cert.img}
                alt={`${cert.issuer} — ${cert.title}`}
                loading="lazy"
                className="aspect-[1.4/1] w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#293891]/30 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#293891] opacity-0 shadow-md transition-all duration-500 group-hover:opacity-100">
                <Maximize2 size={16} />
              </span>
            </div>
            <div className="p-5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#e63329]">{cert.tag}</span>
              <p className="mt-1 font-display text-base font-medium leading-snug text-[#293891]">{cert.title}</p>
            </div>
            <div className="flex items-center gap-2 border-t border-[#293891]/10 px-5 py-3">
              <Award size={15} className="text-[#3B82F6]" />
              <span className="text-xs font-medium text-[#566076]">Issued by {cert.issuer}</span>
            </div>
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-4xl border-[#293891]/10 bg-white p-2 sm:p-3">
          <DialogTitle className="sr-only">{`${cert.issuer} — ${cert.title}`}</DialogTitle>
          <img src={cert.img} alt={`${cert.issuer} — ${cert.title}`} className="h-auto w-full rounded-lg" />
        </DialogContent>
      </Dialog>
    </StaggerItem>
  );
}

export default function Certifications() {
  const { certifications: c } = COURSE;
  let idx = 0;

  return (
    <section id="certifications" data-testid="certifications-section" className="relative py-24 md:py-32">
      <div className="pointer-events-none absolute right-1/4 top-0 h-[400px] w-[400px] glow-blue opacity-50" />
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow>Certifications</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[#293891] sm:text-5xl">
                Certificates that <span className="text-[#e63329]">open doors.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base leading-relaxed text-[#566076]">{c.intro}</p>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <div className="flex flex-wrap gap-2">
              {c.issuers.map((issuer) => (
                <span
                  key={issuer}
                  className="rounded-full border border-[#293891]/10 bg-white px-4 py-2 text-xs font-semibold text-[#293891] shadow-sm"
                >
                  {issuer}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mt-16 space-y-16">
          {c.groups.map((group) => (
            <div key={group.label} data-testid={`cert-group-${group.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Reveal>
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <h3 className="font-display text-2xl font-semibold text-[#293891]">{group.label}</h3>
                  <span className="rounded-full bg-[#3B82F6]/10 px-2.5 py-0.5 text-xs font-semibold text-[#3B82F6]">
                    {group.items.length}
                  </span>
                  <span className="text-sm text-[#566076]">{group.note}</span>
                </div>
              </Reveal>

              <StaggerGroup className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {group.items.map((cert) => {
                  const cur = idx++;
                  return <CertCard key={cert.title + cur} cert={cert} index={cur} />;
                })}

                {group.comingNote && (
                  <StaggerItem>
                    <motion.div
                      whileHover={{ y: -3 }}
                      data-testid="cert-upcoming"
                      className="flex h-full min-h-[220px] flex-col items-start justify-center gap-4 rounded-2xl border border-dashed border-[#293891]/25 bg-white/60 p-6 backdrop-blur-sm"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#3B82F6]/10 text-[#3B82F6]">
                        <Award size={24} />
                      </div>
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[#293891]/10 bg-[#F5F7FC] px-3 py-1 text-[10px] font-medium uppercase tracking-[0.16em] text-[#566076]">
                        <Lock size={10} /> Coming Soon
                      </span>
                      <p className="text-sm leading-relaxed text-[#566076]">{group.comingNote}</p>
                    </motion.div>
                  </StaggerItem>
                )}
              </StaggerGroup>
            </div>
          ))}
        </div>

        {/* Internship letters banner */}
        <Reveal delay={0.1}>
          <div
            data-testid="internship-letters"
            className="mt-16 overflow-hidden rounded-3xl border border-[#293891]/10 bg-[#293891] p-8 md:p-10"
          >
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-md">
                <span className="inline-flex items-center gap-2 rounded-full bg-[#e63329]/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#ff8a80]">
                  <FileCheck size={13} /> Also Included
                </span>
                <h3 className="mt-4 font-display text-2xl font-semibold text-white md:text-3xl">{c.letters.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#c3cee3]">{c.letters.intro}</p>
              </div>
              <div className="grid flex-1 gap-4 sm:grid-cols-3 lg:max-w-2xl">
                {c.letters.items.map((l) => (
                  <div key={l.label} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                    <FileCheck size={20} className="text-[#7fe0b0]" />
                    <p className="mt-3 font-display text-base font-medium leading-snug text-white">{l.label}</p>
                    <p className="mt-1.5 text-xs text-[#8ea0c4]">{l.when}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
